"use client"

import { action, computed, makeObservable, observable, toJS } from "mobx";
import samplesSelectorModel from "../SamplesSelector/SamplesSelectorModel";
import samplesModel, { SAMPLES_MODE, popupCoords } from "../SamplesModel";
import { apiSwagger } from "../../../shared/api";
import { Category, Sample } from "../../../sharedTypes/Api";
import { SamplesUploaderModel } from "../SamplesUploader/SamplesUploaderModel";
import samplesDiagnosticPopupModel from "../SamplesDiagnosticPopup/SamplesDiagnosticPopupModel";
import { notifyStatus } from "@/sharedTypes/coreTypes";
import { dpAssert, getMusicXml } from "@/utils/helpers";
import { readFileAsArrayBuffer } from "@/utils/parse-musicxml/parse-musicxml";
import jsSHA from "jssha";
import { Note } from "@/sharedTypes/main";
import { MeasureTimemap } from "@/sharedTypes/coreTypes";
import { addMidiInfo } from "@/components/Midi/Midi";
import { getCacheFromTimemap } from "@/sharedTypes/pureHelpers";
import { ManagerEntity } from "@/shared/globalFunctions";
import etaLoader from "@/lib/EtaLoader";
import { t } from "@/shared/i18n";
import toast from "@/components/ui/custom-toast";
import { i18n } from "@/sharedTypes/helpers";


export class SamplesEditorModel {
  musicxmlFile: File | null = null;
  encryptedMusicxml: string | null = null;

  audioFile: File | null = null;
  midiFile: File | undefined = undefined;
  cache: string | undefined;

  sample: Sample | undefined = undefined;

  createdCategories: Category[] = [];
  get allCategories(): Category[] {
    return samplesSelectorModel.categories.concat(this.createdCategories)
  }

  samplesEditorRef: HTMLDivElement | null = null;

  /** Whether the loaded MusicXML file is valid (timemap can be computed) */
  musicxmlValid: boolean = true; // Default true for existing samples
  /** Whether MusicXML validation is in progress */
  musicxmlValidating: boolean = false;
  /** Whether the MIDI file is valid (no missed notes) */
  midiValid: boolean = true; // Default true for existing samples
  /** Whether any files have been changed (XML or MIDI uploaded) */
  hasChanges: boolean = false;

  public get title(): string | undefined {
    if (this.sample && this.sample.musicxmlPath)
      return this.sample?.musicxmlFilename;
    return undefined;
  }

  reset() {
    this.musicxmlFile = null;
    this.audioFile = null;
    this.encryptedMusicxml = null;
    this.cache = undefined;
    this.midiFile = undefined;
    this.sample = undefined;
    this.createdCategories = [];
    this.musicxmlValid = true; // Reset to true for next sample
    this.musicxmlValidating = false;
    this.midiValid = true; // Reset to true for next sample
    this.hasChanges = false;
  }

  constructor() {
    makeObservable(this, {
      deleteSelectedSample: action,
      selectSample: action,
      downloadFile: action,
      title: computed,
      musicxmlFile: observable,
      sample: observable,
      encryptedMusicxml: observable,
      audioFile: observable,
      midiFile: observable,
      musicxmlValid: observable,
      musicxmlValidating: observable,
      midiValid: observable,
      hasChanges: observable,
    })
  }
  async deleteSelectedSample() {
    if (this.sample) {
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setContext('deleteXML');
        etaLoader.setStage('deleteXML_process', 0);
      }
      // window.spinner.show("Removing sample...", popupCoords)

      await apiSwagger.samples.midiDelete(this.sample._id);
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('deleteXML_process', 0.3);
      }
      const res = await apiSwagger.samples.samplesDelete(this.sample._id)
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('deleteXML_process', 0.5);
      }
      if (res.ok) {
        window.popup("Deleted sample!", notifyStatus.success, popupCoords)
      }
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('deleteXML_process', 0.75);
      }
      if (this.sample._id === localStorage.getItem("sample")) {
        localStorage.removeItem("sample")
      }
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('deleteXML_process', 0.9);
      }
      await samplesSelectorModel.refreshSamples();
      // window.spinner.hide();
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('deleteXML_process',1);
      }
      samplesModel.currentMode = SAMPLES_MODE.SELECT;
      this.reset();
     }
  }
  async selectSample(sample: Sample) {
    // window.spinner.show("Загрузка...")
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setContext('quickEvent');
      etaLoader.setStage('quickEvent', 0, t("loading.quickEvent.select_sample"));
    }
    this.encryptedMusicxml = null;
    this.cache = undefined;
    this.midiFile = undefined;
    this.musicxmlFile = null;
    samplesDiagnosticPopupModel.startMessageListening()
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('quickEvent', 0.5, t("loading.quickEvent.select_sample"));
    }
    this.sample = sample;
    this.midiValid = (sample.metadata.midiWrongNotesCount ?? 0) === 0;
    await this.updateSheetData();
    ManagerEntity.spinnerMsg = '';
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('quickEvent', 1, t("loading.quickEvent.select_sample"));
    }
  }

  // need to add etaLoader quick action for this too
  async modifySampleCategory(categoryId: string, newName: string) {
    const allCategories: Category[] = this.allCategories;
    const category: Category | undefined = this.allCategories.find(cat => cat._id === categoryId);

    if(category) {
      try {
        const categoriesinDB = await apiSwagger.categories.categoriesList();
        // The response has nested data: categoriesinDB.data.data is the actual array
        const categoriesArray: Category[] = Array.isArray(categoriesinDB.data)
          ? toJS(categoriesinDB.data)
          : toJS((categoriesinDB.data as any)?.data);
        const isCategoryInDB = categoriesArray.some((cat: Category) => cat._id === categoryId);

        if(isCategoryInDB) {
          // update only the current language title, not both
          // Don't spread the whole category - only send the field we want to update
          const updatedCategoryData = {
            [i18n.language]: newName
          };
          const res = await apiSwagger.categories.categoriesPartialUpdate(categoryId, updatedCategoryData);

          if(res.ok) {
            // Update local category to trigger MobX reactivity
            (category as any)[i18n.language] = newName;

            // Force update all samples with this category
            samplesSelectorModel.samples.forEach(sample => {
              if (sample.category._id === categoryId) {
                (sample.category as any)[i18n.language] = newName;
              }
            });
          } else {
            console.error("[SamplesEditor] Failed to update category in DB:", res.error);
          }

        } else {
          // Create new category in DB
          const createData = {
            en: newName,
            ru: newName
          };
          // Only update the current language
          const res = await apiSwagger.categories.categoriesCreate(createData);
          if (res.ok) {
            const newCategory = await res.json();
            // Add to local categories
            this.createdCategories.push(newCategory);
          } else {
            console.error("[SamplesEditor] Error creating category in DB:", res.error);
          }
        }

        await samplesSelectorModel.refreshSamples();
      } catch(err) {
        console.error("[SamplesEditor] Error while modifying category:", err);
        return;
      }
    } else {
      console.error("[SamplesEditor] Category not found for modification:", categoryId);
      return;
    }
  }

  async updateSheetData() {
    if (this.sample) {
      // Only fetch musicxml for download feature - cache is not needed when just opening editor
      this.encryptedMusicxml = await this.getEncryptedMusicXml(this.sample.identifier);
    }
  }

  // This function should return ENCRYPTED (not modified) musicxml
  async getEncryptedMusicXml(sampleIdentifier: string): Promise<string | null> {
    let progress = 0;
    let timer: any;

    // Start progress at 0
    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('preparingMusicEngine', progress);
      timer = setInterval(() => {
        progress = Math.min(progress + 0.01, 0.9);
        etaLoader.setStage('preparingMusicEngine', progress);
      }, 50);
    }

    const response = await apiSwagger.samples.samplesDetail(sampleIdentifier);

    // Stop timer and set progress to 1 when done
    if (typeof window !== 'undefined' && etaLoader) {
      clearInterval(timer);
      etaLoader.setStage('preparingMusicEngine', 1);
    }

    if (response.ok) {
      const data = await response.json();

      if (data.musicxmlUrl) {
        const musicxmlResponse = await fetch(data.musicxmlUrl);
        return await musicxmlResponse.text();
      }
      return '';
    } else {
      window.popup(`error while getting sample: ${sampleIdentifier}`, notifyStatus.failed, popupCoords);
      return null;
    }
  }
  async loadNewMusicxml(file: File) {
    dpAssert(ManagerEntity.core);
    window.spinner.show("Computing cache for the new file...", popupCoords)
    samplesDiagnosticPopupModel.resetWarnings();
    samplesDiagnosticPopupModel.startMessageListening();
    if (!this.sample) { return; }

    this.musicxmlFile = file;
    this.encryptedMusicxml = null;
    this.musicxmlValid = false;
    this.musicxmlValidating = true;

    // upload new file
    const sampleArr: ArrayBuffer = await readFileAsArrayBuffer(file);
    let musicXml;
    try {
      musicXml = await getMusicXml(sampleArr);
    } catch {
      window.popup("Error while building musicxml", notifyStatus.failed)
      this.musicxmlFile = null;
      this.musicxmlValid = false;
      this.musicxmlValidating = false;
      window.spinner.hide();
      return;
    }
    if (musicXml) {
      if (this.midiFile) {
        window.spinner.show("Computing midi for the new file...", popupCoords)
        const cache = await SamplesEditorModel.computeCacheWithDecryptedMusicxmlAndMidi(musicXml, await this.midiFile.arrayBuffer(), this.sample);
        if (!cache) {
          // Error already shown by computeCacheWithDecryptedMusicxmlAndMidi via toast
          window.spinner.hide();
          this.musicxmlFile = null;
          this.musicxmlValid = false;
          this.musicxmlValidating = false;
          return;
        }
        this.cache = JSON.stringify(cache.cache);
        this.midiValid = cache.isMidiValid;
        this.cache = JSON.stringify(cache.cache);
        this.midiValid = cache.isMidiValid;
        this.musicxmlValid = true;
        this.hasChanges = true;
      } else if (this.sample.midiPath) {
        const midi = await samplesSelectorModel.getMidi(this.sample);
        if (midi) {
          window.spinner.show("Computing midi for the new file...", popupCoords)
          const cache = await SamplesEditorModel.computeCacheWithDecryptedMusicxmlAndMidi(musicXml, midi, this.sample);
          if (!cache) {
            // Error already shown by computeCacheWithDecryptedMusicxmlAndMidi via toast
            window.spinner.hide();
            this.musicxmlFile = null;
            this.musicxmlValid = false;
            this.musicxmlValidating = false;
            return;
          }
          this.cache = JSON.stringify(cache.cache);
          this.midiValid = cache.isMidiValid;
          this.musicxmlValid = true;
          this.hasChanges = true;
        }
      } else {
        // No MIDI - cache/timemap will be generated server-side
        // Just mark musicxml as valid for upload
        this.cache = undefined;
        this.musicxmlValid = true;
        this.hasChanges = true;
       }
    }
    this.musicxmlValidating = false;
    window.spinner.hide();
  }
  async downloadFile() {

    if (!this.encryptedMusicxml || !this.sample) {
      console.error("No musicxml specified while tried to get file")
      window.popup("No musicxml specified while tried to get file", notifyStatus.failed)
      return;
    }
    // Create a blob from the MusicXML string
    const blob = new Blob([ManagerEntity.decryptMusicxml(this.encryptedMusicxml)], { type: 'text/xml' });

    // Create a link element
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = this.sample.musicxmlFilename; // Specify the file name here

    // Append the link to the body
    document.body.appendChild(a);

    // Click the link programmatically to trigger the download
    a.click();

    // Remove the link from the body
    document.body.removeChild(a);

    window.spinner.hide()
  }
  async sendUpdate(sample?: Sample) {
    // window.spinner.show("Обновляем...", popupCoords)
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setContext('editXML');
      etaLoader.setStage('editXML_process', 0);
    }

    let success: any = false;
    success = await this.updateSample(sample);
    
    if (success) {
      await samplesSelectorModel.refreshSamples();
    }
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('editXML_process', 1);
    }
    // window.spinner.hide()
    return success;
  }
  async updateSample(sample: Sample | undefined) {
    if (!sample) {
      throw new Error("No sample provided or loaded in model");
    }
    if (!sample) return;
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('editXML_process', 0);
    }
    // window.spinner.show("Обновляем...", popupCoords)
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('editXML_process', 0.1);
    }

    const originalSample = sample ? JSON.parse(JSON.stringify(sample)) : null; //deep copy with check

  // Update category only if changed
  if (
    sample.category &&
    originalSample &&
    (
      sample.category.en !== originalSample.category.en ||
      sample.category.ru !== originalSample.category.ru
    )
  ) {
    await apiSwagger.categories.categoriesPartialUpdate(
      sample.category._id,
      { en: sample.category.en, ru: sample.category.ru }
    );
  }
  if(typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('editXML_process', 0.4);
  }
  // Update author only if changed
  if (
    sample.metadata.author &&  //here the author is the same as pre-change thus it does not update anything
    originalSample &&
    (
      sample.metadata.author.en !== originalSample.metadata.author.en ||
      sample.metadata.author.ru !== originalSample.metadata.author.ru
    )
  ) {
    await apiSwagger.authors.authorsPartialUpdate(
      sample.metadata.author._id,
      { en: sample.metadata.author.en, ru: sample.metadata.author.ru }
    );
  }
  if(typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('editXML_process', 0.75);
  }
  // Now update the sample itself
  const res = await apiSwagger.samples.samplesUpdate({ sample: sample });
  
  if(typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('editXML_process', 0.9);
  }
    if (res.ok) {
      if (this.musicxmlFile && sample) {
        // Cache/timemap is generated server-side after musicxml update
        const sampleArr: ArrayBuffer = await readFileAsArrayBuffer(this.musicxmlFile);
        const musicxml = await getMusicXml(sampleArr)
        dpAssert(musicxml)
        await this.updateMusicxml(this.musicxmlFile.name, musicxml, sample);
      } else if (this.cache) {
        // This is for MIDI sync data update only (no musicxml change)
        await SamplesUploaderModel.replaceCache(sample, JSON.parse(this.cache));
      }
      if (this.audioFile) {
        await this.updateAudio()
      }
      let midiSuccess = true;
      if (this.midiFile) {
        dpAssert(sample)
        midiSuccess = await SamplesEditorModel.updateMidi(this.midiFile, sample);
      }
      
      return midiSuccess;
    } else {
      window.popup('Error, ' + res.error.msg, notifyStatus.failed, popupCoords)
      return false;
    }
  }
  async updateMusicxml(musicxmlFilename: string, musicxml: string, sample: Sample) {
    if (musicxmlFilename && sample && musicxml) {
      // Cache/timemap is generated server-side by verovio-cpp-server
      // No longer sent from client
      const requestBody = {
         musicxml: musicxml,
         musicxmlFilename,
         sampleId: sample._id
      };

      const res = await apiSwagger.samples.musicxmlUpdate(requestBody);

      if (res.ok) {
        const resParsed: any = await res.json();
        return resParsed;
      } else {
        window.popup('error', notifyStatus.failed, popupCoords)
        return undefined;
      }
    }
  }
  async updateAudio() {
    if (this.audioFile && this.sample) {

      const formData = new FormData();
      formData.append('audio', this.audioFile); // Assuming 'file' is a File object from an input element
      formData.append('sampleId', this.sample._id);
      formData.append('audioFilename', this.audioFile.name);

      const res = await apiSwagger.samples.audioCreate(formData as any)
      const data: any = await res.json();
      this.sample.audioFilename = this.audioFile.name;
      this.sample.audioPath = data.audioPath;
      return data;
    }
  }
  loadAudio(file: File) {
    this.audioFile = file;
  }
  async rebuildCaches(onlyMidi = false) {
    // foreach sample rebuild cache
    dpAssert(ManagerEntity.core)
    window.spinner.show("rebuild caches...")
    let samples = samplesSelectorModel.samples
    samples = toJS(samples)
    for (const sample of samples) {
      // reset midi info, we will add it after success loading of midi
      sample.metadata.midiLoaded = false;
      sample.metadata.midiWrongNotesCount = 0;
      try {
        const midi = await samplesSelectorModel.getMidi(sample);;
        if (!midi) {
          if (onlyMidi) {
            // update metadata with sample.metadata.midiLoaded = false etc..
            await samplesEditorModel.updateSample(sample)
            continue;
          }
        }
        window.spinner.show("Computing cache for: " + sample.musicxmlPath)
        const encryptedMusicxml = await this.getEncryptedMusicXml(sample.identifier);
        if (encryptedMusicxml) {
          if (midi) {
           const cache = await SamplesEditorModel.computeCacheWithDecryptedMusicxmlAndMidi(ManagerEntity.decryptMusicxml(encryptedMusicxml), midi, sample);
           await samplesEditorModel.updateSample(sample)
           if (cache)
            await SamplesUploaderModel.replaceCache(sample, cache);
            window.popup("Rebuild one cache!", notifyStatus.success)
          } else {
            const result = await ManagerEntity.core.computeFreshTimemap(encryptedMusicxml);
            await SamplesUploaderModel.replaceCache(sample, result.cache);
            window.popup("Rebuild one cache!", notifyStatus.success)
          }
        } else {
          throw new Error("Failed to get musicxml"); 
        } 
      } catch (err) {
        window.popup("Failed to rebuild cache!", notifyStatus.failed)
        console.error("Failed to rebuild cache:", err);
      }
    }
    window.spinner.hide();
  }
  static async updateMidi(midiFile: File, sample: Sample) {
    if (midiFile) {
      const result = await SamplesEditorModel.sendMidiUpdate(midiFile, sample._id);
      // Check for actual success: midiLoaded must be true AND no processingError
      // Backend returns midiPath even on failure, so we need to check midiLoaded
      if (result && result.midiPath && result.midiLoaded && !result.processingError) {
        sample.midiPath = result.midiPath;
        sample.metadata.midiLoaded = true;
        sample.metadata.midiWrongNotesCount = result.midiWrongNotesCount ?? 0;
        return true;
      }
      // Log the failure for debugging
      if (result && result.processingError) {
        console.error('[SamplesEditorModel] MIDI processing failed:', result.processingError);
      }
      return false;
    }
    return false;
  }
  async midiFileLoaded(file: File) {
    this.midiFile = file;
    dpAssert(this.sample)
    let musicxml: string | null = null;
    if (this.encryptedMusicxml) {
      musicxml = ManagerEntity.decryptMusicxml(this.encryptedMusicxml)
    } else if (this.musicxmlFile) {
      const arr = await readFileAsArrayBuffer(this.musicxmlFile);
      musicxml = await getMusicXml(arr);
    }
    dpAssert(musicxml)
    // compute midi for that this.musicxml and compute this.cache for that this.musicxml
    window.spinner.show("Computing midi for the new file...", popupCoords)
    const result = await SamplesEditorModel.computeCacheWithDecryptedMusicxmlAndMidi(musicxml, await this.midiFile.arrayBuffer(), this.sample);
    if (result) {
      this.cache = JSON.stringify(result.cache);
      this.midiValid = result.isMidiValid; // Update MIDI validity
      this.hasChanges = true;
    }
    if (!result) {
      window.popup("Bad midi file", notifyStatus.failed, popupCoords)
      this.midiFile = undefined;
      this.midiValid = false;
    }
    window.spinner.hide()
  }
  static async computeCacheWithDecryptedMusicxmlAndMidi(musicxml: string, midiBuffer: ArrayBuffer, sample: Sample): Promise<any> {
    // todo incomment that in prod
    //
    dpAssert(ManagerEntity.core)

    // we should pass false as a second param for computeFreshTimemap if we pass unencrypted musicxml
    let result;
    try {
      result = await ManagerEntity.core.computeFreshTimemap(musicxml, false);
    } catch (error) {
      console.error('Error computing timemap in computeCacheWithDecryptedMusicxmlAndMidi:', error);
      toast({
        title: t('error'),
        description: t('toast.timemap_computation_error_desc'),
        type: 'error',
      });
      return undefined;
    }
    const timemap: MeasureTimemap | undefined = result.timemap;
    const allNotes: Note[] | undefined = result.allNotes;
    const notesHashMap: Record<string, Note> | undefined = result.noteHashMap;

    dpAssert(timemap, "Failed to get timemap in computeCacheWithMidi")
    dpAssert(allNotes, "Failed to get allNotes in computeCacheWithMidi")
    dpAssert(notesHashMap)

    const countOfMissedNotes = await addMidiInfo(midiBuffer, timemap, allNotes, notesHashMap)
    // MIDI is valid only if there are no missed notes
    const isMidiValid = countOfMissedNotes === 0;
    if (countOfMissedNotes != -1) {
      sample.metadata.midiLoaded = true;
      sample.metadata.midiWrongNotesCount = countOfMissedNotes;
      const cache = getCacheFromTimemap(timemap);
      return { cache, isMidiValid };
    } else {
      return undefined;
    }
  }
  static async sendMidiUpdate(file: File, sampleId: string) {
    // Timeout for MIDI processing (20 seconds) - same as backend timeout
    const MIDI_TIMEOUT_MS = 20000;

    try {
      // Create FormData object - apiSwagger expects FormData, not a plain object
      const formData = new FormData();
      formData.append('midi', file);
      formData.append('fileName', file.name);
      formData.append('sampleId', sampleId);

      // Race between the actual request and a timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('MIDI processing timeout - server took too long')), MIDI_TIMEOUT_MS);
      });

      const res = await Promise.race([
        apiSwagger.samples.midiCreate(formData as any),
        timeoutPromise
      ]);

      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        const errorData = await res.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error('MIDI upload error:', res.status, errorData);
        return false;
      }
    } catch (error: any) {
      console.error('MIDI upload exception:', error?.message || error);
      return false;
    }
  }
}
const samplesEditorModel = new SamplesEditorModel
export default samplesEditorModel
